import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { FileUpload } from './components/FileUpload';
import { DataVisualization } from './components/DataVisualization';
import { PollingStatusPage } from './components/PollingStatusPage';
import { AccessCodeLogin } from './components/AccessCodeLogin';
import { loadStoredCode } from './store/authSlice';
import { RootState, AppDispatch } from './store/store';
import { COLORS } from './constants/colors';
import './App.css';

const AuthGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);

  useEffect(() => {
    // If not authenticated and trying to access protected routes, redirect to homepage
    if (!isAuthenticated && location.pathname !== '/') {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, location.pathname, navigate]);

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <AccessCodeLogin />;
  }

  return <>{children}</>;
};

function App() {
  const dispatch = useDispatch<AppDispatch>();
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);

  useEffect(() => {
    document.documentElement.style.setProperty('--primary-color', COLORS.PRIMARY);
    document.documentElement.style.setProperty('--secondary-color', COLORS.SECONDARY);
    
    // Check for stored access code on app start
    const storedCode = localStorage.getItem('auth_access_code');
    if (storedCode && !isAuthenticated) {
      dispatch(loadStoredCode(storedCode));
    }
  }, [dispatch, isAuthenticated]);

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: COLORS.SECONDARY,
          colorPrimaryHover: COLORS.PRIMARY,
        },
      }}
    >
      <div className="App">
        <Router>
          <AuthGuard>
            <Routes>
              <Route path="/" element={<FileUpload />} />
              <Route path="/upload/:flowId" element={<DataVisualization />} />
              <Route path="/polling/:flowId" element={<PollingStatusPage />} />
            </Routes>
          </AuthGuard>
        </Router>
      </div>
    </ConfigProvider>
  );
}

export default App;
